import {useCallback, useEffect, useRef, useState} from 'react';

import {AdcConjugationMethod} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  ConjugationSite,
  STOCHASTIC_CONJUGATION_METHODS,
} from 'molbio/adcs/conjugation-sites/AdcConjugationSiteConstants';
import {serializeConjugationSites} from 'molbio/adcs/conjugation-sites/AdcConjugationSiteSerialization';
import {getConjugationMethodFromTypedValue} from 'molbio/adcs/conjugation-sites/AdcConjugationSiteUtils';
import {AntibodyDomain} from 'molbio/adcs/conjugation-sites/antibodyDomainsQuery';
import {
  ConjugationSiteCandidatesResult,
  findConjugationSiteCandidates,
} from 'molbio/adcs/conjugation-sites/findConjugationSiteCandidates';
import useAntibodyDomains from 'molbio/adcs/conjugation-sites/useAntibodyDomains';
import {StructuredTableColumnGroups} from 'notebook/structured-tables/StructuredTableConstants';
import {
  findColumnIndexByGroup,
  getBioentityIdFromEvaluatedCell,
} from 'notebook/tables/search-scoping/rowScopedSearch';
import {RowModelContext} from 'notebook/tables/store/contexts/RowModelContext';
import {useRowModel} from 'notebook/tables/store/hooks/useRowModel';
import {useTableMutations} from 'notebook/tables/store/hooks/useTableMutations';

export interface AdcConjugationSitesCellState {
  /** The row's protein, or null if it is empty or invalid. */
  antibodyId: string | null;
  /** The row's conjugation method, or null if it is empty or invalid. */
  conjugationMethod: AdcConjugationMethod | null;
  /** The candidate sites for the row's protein and method, or null until they are found. */
  candidates: ConjugationSiteCandidatesResult | null;
  /** The row's antibody's domains for labeling sites, or null while they load or if they can't be read. */
  domains: Array<AntibodyDomain> | null;
  /** Commits a new selection of sites through the cell's `onUpdate`. */
  updateSites: (newSites: Array<ConjugationSite>) => void;
}

interface FoundCandidates {
  antibodyId: string;
  conjugationMethod: AdcConjugationMethod;
  result: ConjugationSiteCandidatesResult;
}

/**
 * Manages a Conjugation sites cell in an ADC registration table: finds the candidate sites for the row's
 * Protein and Conjugation method, loads the protein's domains to label sites, commits the sites the user chooses, and clears the selection when the method
 * changes to a different method. An empty method is not treated as a change, so the cell keeps its sites while the method
 * is still being evaluated. Only changes made while the cell is rendered are seen; the column's custom eval
 * flags sites left over from any other change.
 *
 * Choosing a stochastic method (see STOCHASTIC_CONJUGATION_METHODS) for an empty cell writes every candidate site
 * to the cell once they're found, so the user can still narrow or clear the selection. Only a choice made while the
 * cell is rendered does this: an empty cell always means no sites are selected, so a cell that renders with the
 * method already chosen, e.g. when the entry is reopened, is left as it is.
 *
 * Clearing and selecting every candidate write the cell directly rather than through `onUpdate`, the cell's
 * selector handler, which would also select it. Must be called inside a table row (see RowModelProvider).
 *
 * @param sites - the cell's selected sites
 * @param onUpdate - commits the cell's new text when the user changes the selection
 */
export default function useAdcConjugationSitesCell(
  sites: Array<ConjugationSite>,
  onUpdate: (text: string) => void
): AdcConjugationSitesCellState {
  const {rowIndex} = RowModelContext.useContext();
  const {updateCell} = useTableMutations();
  const columnIndex = useRowModel(({columnConstraints}) =>
    findColumnIndexByGroup(columnConstraints, StructuredTableColumnGroups.ADC_CONJUGATION_SITES)
  );
  const antibodyId = useRowModel(({columnConstraints, evaluatedRow}) =>
    getBioentityIdFromEvaluatedCell(
      evaluatedRow[findColumnIndexByGroup(columnConstraints, StructuredTableColumnGroups.ADC_PROTEIN)]
    )
  );
  const domains = useAntibodyDomains(antibodyId);
  const conjugationMethod = useRowModel(({columnConstraints, evaluatedRow}) =>
    getConjugationMethodFromTypedValue(
      evaluatedRow[
        findColumnIndexByGroup(columnConstraints, StructuredTableColumnGroups.ADC_CONJUGATION_METHOD)
      ]?.value
    )
  );

  const [found, setFound] = useState<FoundCandidates | null>(null);
  useEffect(() => {
    if (antibodyId == null || conjugationMethod == null) {
      return undefined;
    }
    let isCurrent = true;
    findConjugationSiteCandidates({antibodyId, conjugationMethod}).then((result) => {
      if (isCurrent) {
        setFound({antibodyId, conjugationMethod, result});
      }
    });
    return () => {
      isCurrent = false;
    };
  }, [antibodyId, conjugationMethod]);
  // Candidates found for earlier inputs are never returned.
  const candidates =
    found != null && found.antibodyId === antibodyId && found.conjugationMethod === conjugationMethod
      ? found.result
      : null;

  const sitesText = serializeConjugationSites(sites);
  const lastMethodRef = useRef(conjugationMethod);
  // The method the cell was last cleared for, whose clearing the cell's sites may not reflect yet.
  const clearedForMethodRef = useRef<AdcConjugationMethod | null>(null);
  // The stochastic method chosen while the cell was rendered, whose candidates are yet to be selected.
  const selectAllForMethodRef = useRef<AdcConjugationMethod | null>(null);
  useEffect(() => {
    if (conjugationMethod == null || conjugationMethod === lastMethodRef.current) {
      return;
    }
    const lastMethod = lastMethodRef.current;
    lastMethodRef.current = conjugationMethod;
    selectAllForMethodRef.current = STOCHASTIC_CONJUGATION_METHODS.has(conjugationMethod)
      ? conjugationMethod
      : null;
    if (lastMethod != null && sitesText !== '') {
      clearedForMethodRef.current = conjugationMethod;
      updateCell([rowIndex, columnIndex], {text: ''});
    }
  }, [conjugationMethod, sitesText, updateCell, rowIndex, columnIndex]);

  // Waits for the chosen method's candidates, including until a protein is chosen, and selects them once, so a
  // selection the user then narrows or clears is kept.
  useEffect(() => {
    if (
      conjugationMethod == null ||
      selectAllForMethodRef.current !== conjugationMethod ||
      candidates == null ||
      !('candidates' in candidates)
    ) {
      return;
    }
    selectAllForMethodRef.current = null;
    const isEmpty = sitesText === '' || clearedForMethodRef.current === conjugationMethod;
    clearedForMethodRef.current = null;
    if (isEmpty && candidates.candidates.length > 0) {
      updateCell([rowIndex, columnIndex], {text: serializeConjugationSites(candidates.candidates)});
    }
  }, [conjugationMethod, candidates, sitesText, updateCell, rowIndex, columnIndex]);

  const updateSites = useCallback(
    (newSites: Array<ConjugationSite>) => {
      onUpdate(serializeConjugationSites(newSites));
    },
    [onUpdate]
  );

  return {antibodyId, conjugationMethod, candidates, domains, updateSites};
}
